<template>
    <Transition name="fade">
        <div
            class="card card__list list-grid__item"
            v-if="list && !isHidden"
            v-click-outside="clickOutside"
        >
            <div class="card__container">
                <!--Processing text-->
                <div class="card__processing" v-if="isProcessing">
                    <div class="card__processing-label">
                        {{ isProcessingText }}
                    </div>
                </div>
                <!--Bookmarks-->
                <template v-if="isBookmarksView">
                    <div class="card__bookmark-owner">
                        <div class="owner-avatar">
                            <a
                                :href="owner.open_graph.url"
                                :title="owner.username"
                                v-if="owner"
                                ><img
                                    :src="owner.avatar.profile_photo"
                                    height="26"
                                    width="26"
                                    :alt="owner.presentation_name"
                            /></a>
                        </div>
                        <div class="owner-message">
                            List by
                            <a
                                :href="owner.open_graph.url"
                                :title="owner.username"
                                v-if="owner"
                                >@{{ owner.username }}</a
                            >
                        </div>
                    </div>
                </template>
                <!--Card Anchor-->
                <Link
                    :href="route('list.show', { giftlist: list.uuid })"
                    class="card__anchor"
                >
                    <!--Card Image(s)-->
                    <div class="card__images card__images--multiple">
                        <template v-if="useLazyLoadOnImages">
                            <VLazyImage
                                class="card__image card__images-left"
                                :src="list.image_url[0]"
                            />
                            <div class="card__images-right">
                                <VLazyImage
                                    class="card__image image-01"
                                    :src="list.image_url[1]"
                                />
                                <VLazyImage
                                    class="card__image image-02"
                                    :src="list.image_url[2]"
                                />
                            </div>
                        </template>
                        <template v-else>
                            <div
                                class="card__image card__images-left"
                                :style="{
                                    backgroundImage:
                                        'url(' + list.image_url[0] + ')',
                                }"
                            ></div>
                            <div class="card__images-right">
                                <div
                                    class="image-01 card__image"
                                    :style="{
                                        backgroundImage:
                                            'url(' + list.image_url[1] + ')',
                                    }"
                                ></div>
                                <div
                                    class="image-02 card__image"
                                    :style="{
                                        backgroundImage:
                                            'url(' + list.image_url[2] + ')',
                                    }"
                                ></div>
                            </div>
                        </template>
                    </div>
                    <!--Card Text-->
                    <div class="card__txt">
                        <div class="card__txt--count">
                            {{ list.item_count }}
                        </div>
                        <div class="card__txt--name">{{ list.name }}</div>
                    </div>
                </Link>
                <!--Card Tools-->
                <footer class="card__tools">
                    <template v-if="isGiftHeroExpertList">
                        <div class="card__tools--icon-label">
                            <span class="icon">
                                <svg
                                    width="100"
                                    height="100"
                                    version="1.1"
                                    viewBox="0 0 100 100"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <g fill-rule="evenodd">
                                        <path
                                            d="m50 12.5c-20.711 0-37.5 16.789-37.5 37.5s16.789 37.5 37.5 37.5 37.5-16.789 37.5-37.5-16.789-37.5-37.5-37.5zm-45.832 37.5c0-25.312 20.52-45.832 45.832-45.832s45.832 20.52 45.832 45.832-20.52 45.832-45.832 45.832-45.832-20.52-45.832-45.832z"
                                        />
                                        <path
                                            d="m50 27.383c-4.2734 0-7.7383 3.4648-7.7383 7.7383 0 4.2734 3.4648 7.7383 7.7383 7.7383s7.7383-3.4648 7.7383-7.7383c0-4.2734-3.4648-7.7383-7.7383-7.7383zm-16.07 7.7383c0-8.8789 7.1953-16.074 16.07-16.074 8.8789 0 16.074 7.1953 16.074 16.074 0 8.875-7.1953 16.07-16.074 16.07-8.875 0-16.07-7.1953-16.07-16.07z"
                                        />
                                        <path
                                            d="m50 62.5c-11.562 0-21.301 7.8516-24.152 18.523-0.59375 2.2227-2.8789 3.543-5.1016 2.9492s-3.543-2.8789-2.9492-5.1016c3.8008-14.223 16.773-24.703 32.203-24.703s28.402 10.48 32.207 24.703c0.59375 2.2227-0.72656 4.5078-2.9492 5.1016-2.2266 0.59375-4.5078-0.72656-5.1016-2.9492-2.8555-10.672-12.594-18.523-24.156-18.523z"
                                        />
                                    </g>
                                </svg>
                            </span>
                            <span class="label">{{ list.user.full_name }}</span>
                        </div>
                    </template>
                    <template v-else>
                        <div
                            class="card__tools--icon-label"
                            v-if="list.privacy && list.privacy !== ''"
                        >
                            <span class="icon">
                                <component
                                    :is="
                                        list.privacy === 'public'
                                            ? SvgCardPrivacyPublic
                                            : SvgCardPrivacyPrivateLink
                                    "
                                />
                            </span>
                            <span class="label">{{ privacyLabel }}</span>
                        </div>
                    </template>
                    <!--List Card Option Menu-->
                    <list-card-option-menu
                        v-if="showListCardOptionsMenu"
                        :list="props.list"
                        :owner="props.owner"
                    ></list-card-option-menu>
                </footer>
            </div>
        </div>
    </Transition>
</template>
<script setup>
import { computed, ref } from "vue";
import { Link } from "@inertiajs/vue3";
import { useCardOptionsMenuStore } from "@/stores/cardOptionsMenu";
import ListCardOptionMenu from "@/Components/OptionMenus/ListCardOptionMenu.vue";
import SvgCardPrivacyPublic from "~/svg/ui-card-privacy-public.svg";
import SvgCardPrivacyPrivateLink from "~/svg/ui-card-privacy-private-link.svg";
import VLazyImage from "v-lazy-image";

const cardOptionsMenuStore = useCardOptionsMenuStore();

const props = defineProps({
    list: Object,
    owner: Object,
    showListCardOptionsMenu: {
        type: Boolean,
        default: true,
    },
});

// Props
// const { list, owner } = toRefs(props);

// Local state
const isDeleted = ref(false);
const isHidden = ref(false);
const useLazyLoadOnImages = ref(false);

// Computed properties
const isBookmarksView = computed(() => {
    return route().current() === "bookmarks";
});

const isGiftHeroExpertList = computed(() => {
    return (
        props.list.type === "expert" &&
        props.list.hasOwnProperty("user") &&
        props.list.user?.is_gift_hero === 1
    );
});

const isProcessing = computed(() => {
    return props.list.hasOwnProperty("is_processing")
        ? props.list.is_processing
        : false;
});

const isProcessingText = computed(() => {
    return props.list?.is_processing_text ?? "Processing";
});

const listName = computed(() => {
    return props.list.name.substring(0, 70);
});

const privacyLabel = computed(() => {
    switch (props.list.privacy) {
        case "private":
            return "Private Link";
            break;
        case "public":
            return "Public";
            break;
        default:
            return null;
    }
});

const clickOutside = () => {
    cardOptionsMenuStore.closeAll();
};
</script>
